import './VoteQuestionWithSignExecutor.scss';
import '../../Signing.scss';
import React, { useEffect, useState } from 'react';
import { ICertificate } from '@models/certificates/cert';
import { ParticipantVoteType } from '@models/document/actionsExecution/ParticipantVoteType';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { IAbookOptions } from '@models/Forms/IAbookOptions';
import { IVoteWithSignResult, IVotingWithSignModel } from '@models/document/actionsExecution/IVotingWithSignModel';
import { IFormValues } from '@models/Forms/IForms';
import { getABookFormValues } from '@utils/abookUtils';
import { CadespluginService } from '@services/CadespluginService';
import { DocumentExecutionService } from '@services/actions/DocumentExecutionService';
import { utf8ToBase64 } from '@srm/ui/lib/base64';
import { useForm } from 'react-hook-form';
import ModalContainer, { IModalContainerProps } from '@molecules/ModalContainer/ModalContainer';
import FormGroup from '@srm/ui/form-group';
import Abookpicker from '@atoms/Abookpicker';
import { IAddressBookData } from '@models/addressbook/IAddressBookData';
import Radio from '@srm/ui/radio';
import Box from '@atoms/Box';
import { safeFormatDate } from '@utils/date';
import FatalError, { FatalErrorIcon } from '@srm/ui/fatal-error';
import TextareaControl from '@srm/ui/controls/TextareaControl';
import { requiredTextRules } from '@srm/ui/lib/requiredTextRules';
import { IVoteWithSignProps } from './IVoteWithSignProps';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';
import { REMOTE_CONTROL_EXCLUDE_CLASS } from '@srm/screen-sharing/light';
import { useDocumentAbookTabs } from '@/hooks/useDocumentAbookTabs';

interface IVoteWithSignFormData {
    comment?: string;
}

export const VoteQuestionWithSignExecutorModal: React.FC<IVoteWithSignProps> = (props: IVoteWithSignProps) => {
    const [errorText, setErrorText] = useState<string>();
    // Подготовка (сертификаты, модель) и подписание голоса — разные состояния: подготовку можно
    // бросить закрытием окна, подписание — нет.
    const [loading, setLoading] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [certList, setCertList] = useState<ICertificate[]>();

    const [selectedCert, setSelectedCert] = useState<ICertificate>();

    const [decision, setDecision] = useState<ParticipantVoteType>();
    const [participantId, setParticipantId] = useState<number>();
    const [participantName, setParticipantName] = useState<string>();
    const user = useTypedSelector((state) => state.auth.user);

    const [abookOptions, setAbookOptions] = useState<IAbookOptions>();
    const [jsonModelsToSignDict, setJsonModelsToSignDict] = useState<Record<string, IVotingWithSignModel>>();
    const abookTabs = useDocumentAbookTabs(Object.keys(jsonModelsToSignDict ?? {})[0], abookOptions?.tabs);

    const [formValues, setFormValues] = useState<string>('{}');
    const activated = React.useRef(false);

    const formMethods = useForm<IVoteWithSignFormData>({ mode: 'onBlur' });
    const hasUnsavedInput = useUnsavedFormInput(formMethods, { textFields: ['comment'] });

    const commentIsRequired = decision === ParticipantVoteType.No;

    const getFormValues = async (docId: string, fValues?: IFormValues) => {
        const vals = await getABookFormValues(docId, fValues);
        if (activated.current && vals) {
            setFormValues(vals);
        }
    };

    const loadCertificates = async (isIgnored: () => boolean) => {
        const serv = new CadespluginService();
        const certs = await serv.getCertListByAlgSubstring(['rsa'], []); //тут только rsa
        if (isIgnored()) return;
        //если сертификат один - сразу делаем его выбранным
        if (certs && certs.length === 1) {
            setSelectedCert(certs[0]);
        }
        setCertList(certs);
    };

    useEffect(() => {
        // Закрытое окно ответы подготовки не применяет.
        let ignore = false;
        setLoading(true);
        activated.current = true;

        loadCertificates(() => ignore).catch((error) => {
            if (!ignore) setErrorText(error);
        });

        const serv = new DocumentExecutionService(props.docId);
        serv.getVotingWithSignModel(props.action.key)
            .then((res) => {
                if (ignore) return;
                const dict = res.data;
                const firstDocId = Object.keys(dict)[0];
                const abookOptions = dict[firstDocId].aBookOptions;
                setAbookOptions(abookOptions);
                void getFormValues(firstDocId, abookOptions?.formValues);
                setJsonModelsToSignDict(dict);
            })
            .catch((error) => {
                if (!ignore) setErrorText(error);
            })
            .finally(() => {
                if (!ignore) setLoading(false);
            });

        // Если голосующий Участник комиссии, то берем голос из конфигурации и записываем голосующим текущего пользователя
        if (props.userType === 'participant') {
            switch (props.participantVote) {
                case 'yes':
                    setDecision(ParticipantVoteType.Yes);
                    break;
                case 'no':
                    setDecision(ParticipantVoteType.No);
                    break;
            }

            setParticipantId(+user.id);
            setParticipantName(user.fullName);
        }
        return () => {
            ignore = true;
            activated.current = false;
        };
    }, []);

    const onDecisionChanged = (e: any) => {
        const num = +e.target.value;
        const vote = num;
        setDecision(vote);
    };

    const onSubmit = async (form: IVoteWithSignFormData) => {
        if (selectedCert && decision !== undefined && participantId) {
            setErrorText(undefined);
            try {
                setIsSubmitting(true);

                const voteDataRecord: Record<string, IVoteWithSignResult> = {};

                for (const key in jsonModelsToSignDict) {
                    jsonModelsToSignDict[key].jsonModel.comment = form.comment ?? '';
                    jsonModelsToSignDict[key].jsonModel.vote = decision;

                    const jsonToSign: string = JSON.stringify(jsonModelsToSignDict[key].jsonModel);
                    const jsonToSignBase64 = utf8ToBase64(jsonToSign);
                    const cadesServ = new CadespluginService();
                    const sign = await cadesServ.signData(jsonToSignBase64, selectedCert.thumbprint);

                    const voteResult: IVoteWithSignResult = {
                        jsonModel: jsonToSign,
                        jsonModelBase64: jsonToSignBase64,
                        sign: sign.sign,
                        voteType: decision,
                        participantId: participantId,
                        certThumbprint: selectedCert.thumbprint,
                        certValidFrom: selectedCert.validFrom,
                        certValidTo: selectedCert.validTo,
                        certSerialNumber: selectedCert.serialNumber,
                        certIssuedToName: selectedCert.subjectName,
                        certIssuedToEmail: selectedCert.issuedToEmail,
                    };

                    voteDataRecord[key] = voteResult;
                }

                const execServ = new DocumentExecutionService(props.docId);
                await execServ.sendSignedVote(props.action.key, voteDataRecord);

                if (props.completeHandler) props.completeHandler(true);
            } catch (e: any) {
                if (typeof e === 'string') {
                    setErrorText(e);
                } else if (Array.isArray(e)) {
                    const errorText = e.join('\n');
                    setErrorText(errorText);
                } else {
                    const errorText =
                        'Ошибка при подписании. Убедитесь, что в браузере установлен и настроен плагин КриптоПро ЭЦП. ' +
                        'Проверить работу плагина: https://cryptopro.ru/sites/default/files/products/cades/demopage/cades_bes_sample.html';
                    setErrorText(errorText);
                }
                console.error(e);
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const modalContainerProps: IModalContainerProps = {
        header: props.action.displayName,
        size: props.modalSize,
        // Электронную подпись ставит сам пользователь: удалённое управление поддержки окно не нажимает.
        className: REMOTE_CONTROL_EXCLUDE_CLASS,
        showOkButton: true,
        showCancelButton: true,
        okButtonText: props.okButtonText,
        cancelButtonText: props.cancelButtonText,
        loading: loading || isSubmitting,
        closeDisabled: isSubmitting,
        hasUnsavedInput: () => hasUnsavedInput() || (props.userType === 'secretary' && participantId !== undefined) || ((props.userType === 'secretary' || props.participantVote === 'selectable') && decision !== undefined) || selectedCert?.thumbprint !== (certList?.length === 1 ? certList[0].thumbprint : undefined),
        errorText: errorText,
        warningText:
            jsonModelsToSignDict && Object.keys(jsonModelsToSignDict).length > 1
                ? 'Голос будет применен ко всем выбранным вопросам!'
                : undefined,
        okButtonDisabled:
            !selectedCert || (props.userType === 'secretary' && !participantId) || decision === undefined,
        formMethods: formMethods,
        onSubmit: onSubmit,
        onCancel: () => {
            if (props.completeHandler) props.completeHandler(false);
        },
    };

    return (
        <ModalContainer {...modalContainerProps}>
            {props.userType === 'secretary' && (
                <FormGroup label="Участник комиссии" className="frmGroup">
                    <Abookpicker
                        isMultiple={false}
                        title="Участник комиссии"
                        displayValue={participantName ?? ''}
                        formValues={formValues}
                        name="participant"
                        showChips={false}
                        tabsSettings={abookTabs}
                        onChange={async (value: IAddressBookData[]) => {
                            if (value && value.length === 1) {
                                setParticipantId(value[0].key);
                                setParticipantName(value[0].fullName);
                            }
                        }}
                    />
                </FormGroup>
            )}
            {(props.userType === 'secretary' ||
                (props.userType === 'participant' && props.participantVote === 'selectable')) && (
                <FormGroup label="Решение" className="frmGroup">
                    <Radio name="decision" label="За" value="1" onChange={onDecisionChanged} />
                    <Radio name="decision" label="Против" value="0" onChange={onDecisionChanged} />
                </FormGroup>
            )}
            <FormGroup label="Сертификаты" className="frmGroup frmGroupCerts">
                <div className="certList">
                    {certList && certList.length > 0 ? (
                        certList.map((cert, i) => (
                            <Box key={i.toString()} className="certificateBox">
                                <input
                                    type="radio"
                                    name="certSelection"
                                    value={cert.thumbprint}
                                    onChange={(e) => {
                                        setSelectedCert(certList.find((x) => x.thumbprint === e.target.value));
                                    }}
                                    checked={cert.thumbprint === selectedCert?.thumbprint}
                                ></input>
                                <div>
                                    <div>
                                        <b>Владелец сертификата </b>
                                        {cert.subjectName}
                                    </div>
                                    <div>
                                        <b>Центр выдачи сертификата </b>
                                        {cert.issuerName}
                                    </div>
                                    <div>
                                        <b>Действителен с </b>
                                        {safeFormatDate(cert.validFrom, 'DD.MM.YYYY')}
                                    </div>
                                    <div>
                                        <b>Действителен по </b>
                                        {safeFormatDate(cert.validTo, 'DD.MM.YYYY')}
                                    </div>
                                </div>
                            </Box>
                        ))
                    ) : (
                        <div className="certList--noCertsAvailable">
                            <FatalError
                                icon={<FatalErrorIcon variant="default" size={{ width: 100, height: 100 }} />}
                                title="Нет доступных сертификатов"
                                description=""
                            />
                        </div>
                    )}
                </div>
            </FormGroup>

            <TextareaControl
                name="comment"
                label="Комментарий"
                required={commentIsRequired}
                rows={6}
                control={formMethods.control}
                rules={commentIsRequired ? requiredTextRules() : undefined}
            />
        </ModalContainer>
    );
};
